<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="380"
    persistent
    overlay-opacity="0.55"
    overlay-color="#0F172A"
    content-class="confirm-modal-root"
  >
    <div class="cm-card" :class="isDark ? 'cm-dark' : 'cm-light'">
      <!-- Header -->
      <div class="cm-header">
        <h3 class="cm-title">{{ title }}</h3>
        <!-- Close button -->
        <button class="cm-close" @click="onCancel" type="button" aria-label="Close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <!-- Icon rings (pulse animation) -->
      <div class="cm-icon-outer" :style="{ background: cfg.bgSoft }">
        <div class="cm-icon-inner" :style="{ background: cfg.bgMedium }">
          <v-icon :style="{ color: cfg.color }" size="26">{{ cfg.icon }}</v-icon>
        </div>
      </div>

      <!-- Text -->
      <p class="cm-message">{{ message }}</p>

      <!-- Buttons -->
      <div class="cm-actions">
        <button class="cm-btn-cancel" @click="onCancel" type="button">
          {{ cancelText }}
        </button>
        <button
          class="cm-btn-confirm"
          :style="{ background: cfg.gradient, 'box-shadow': cfg.shadow }"
          :disabled="loading"
          @click="onConfirm"
          type="button"
        >
          <v-icon v-if="loading" size="16" class="cm-spinner">mdi-loading</v-icon>
          <span v-else>{{ confirmText }}</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'ConfirmModal',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
     btnColor: { type: String, default: '#6366f1' }, 
    value: { type: Boolean, default: false },
    type: {
      type: String,
      default: 'danger',
      validator: v => ['danger', 'warning', 'primary', 'logout'].includes(v),
    },
    title: { type: String, default: 'Confirm Action' },
    message: { type: String, default: 'Are you sure you want to proceed?' },
    confirmText: { type: String, default: 'Confirm' },
    cancelText: { type: String, default: 'Cancel' },
    loading: { type: Boolean, default: false },
  },

  emits: ['input', 'confirm', 'cancel'],

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor', 'buttonColor']),

  cfg() {
  const base = this.btnColor !== '#6366f1' ? this.btnColor : (this.buttonColor || this.primaryColor || '#6366f1');

  const common = {
    icon: 'mdi-help-circle-outline',
    color: base,
    bgSoft: this.alpha(base, 0.08),
    bgMedium: this.alpha(base, 0.15),
    gradient: `linear-gradient(135deg, ${this.lighten(base, 20)}, ${base})`,
    shadow: `0 10px 20px ${this.alpha(base, 0.25)}`
  };

  const map = {
    danger: {
      icon: 'mdi-trash-can-outline',
      color: '#ef4444',
      bgSoft: 'rgba(239,68,68,0.08)',
      bgMedium: 'rgba(239,68,68,0.15)',
      gradient: 'linear-gradient(135deg,#ef4444,#b91c1c)',
      shadow: '0 10px 20px rgba(239,68,68,0.25)',
    },

    warning: {
      ...common,
      icon: 'mdi-alert-outline',
    },

    primary: {
      ...common,
      icon: 'mdi-help-circle-outline',
    },

    logout: {
      ...common,
      icon: 'mdi-logout',
    },
  };

  return map[this.type] || common;
}
  },

  methods: {
     lighten(hex, amt = 40) {
    let r = Math.min(255, parseInt(hex.slice(1, 3), 16) + amt);
    let g = Math.min(255, parseInt(hex.slice(3, 5), 16) + amt);
    let b = Math.min(255, parseInt(hex.slice(5, 7), 16) + amt);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  },
  alpha(hex, opacity) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  },
    onConfirm() {
      this.$emit('confirm');
    },
    onCancel() {
      this.$emit('input', false);
      this.$emit('cancel');
    },
  },
};
</script>

<style>
/* global: needed for v-dialog content class */
.confirm-modal-root {
  border-radius: 24px !important;
  overflow: visible !important;
  box-shadow: none !important;
  background: transparent !important;
}
.v-overlay--active .v-overlay__scrim {
  backdrop-filter: blur(8px) !important;
}
</style>

<style scoped>
.cm-card {
  position: relative;
  border-radius: 24px;
  padding: 24px 28px 28px;
  text-align: center;
  overflow: hidden;
  animation: cmFadeIn 0.22s cubic-bezier(0.4,0,0.2,1);
}

.cm-light {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.12);
}
.cm-dark {
  background: #1E293B;
  border: 1px solid rgba(255,255,255,0.07);
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.45);
}

/* Header */
.cm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

/* Close button */
.cm-close {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94A3B8;
  opacity: 0.6;
  transition: all 0.2s;
}
.cm-close:hover {
  opacity: 1;
  background: rgba(0,0,0,0.05);
  transform: rotate(90deg);
}

/* Icon rings */
.cm-icon-outer {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  animation: cmPulse 2.4s ease-in-out infinite;
}
.cm-icon-inner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Text */
.cm-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #111827;
  margin: 0;
}
.cm-dark .cm-title {
  color: #F1F5F9;
}
.cm-message {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.55;
  margin: 0 0 28px;
  padding: 0 8px;
}

/* Action buttons */
.cm-actions {
  display: flex;
  flex-direction: row;
  gap: 12px;
}
.cm-btn-confirm {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 14px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'Instrument Sans', sans-serif;
}
.cm-btn-confirm:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: translateY(-2px);
}
.cm-btn-confirm:active:not(:disabled) {
  transform: translateY(0);
}
.cm-btn-confirm:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.cm-btn-cancel {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 14px;
  background: #F1F5F9;
  color: #6B7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  font-family: 'Instrument Sans', sans-serif;
}
.cm-dark .cm-btn-cancel {
  background: rgba(255,255,255,0.07);
  color: #94A3B8;
}
.cm-btn-cancel:hover {
  background: #E2E8F0;
  color: #374151;
}
.cm-dark .cm-btn-cancel:hover {
  background: rgba(255,255,255,0.12);
  color: #F1F5F9;
}

/* Spinner */
.cm-spinner {
  animation: cmSpin 0.8s linear infinite;
}

/* Animations */
@keyframes cmPulse {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.06); }
}
@keyframes cmFadeIn {
  from { opacity: 0; transform: scale(0.94) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes cmSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
